import React, {
  useContext,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { EmptyState } from "@/modules/common/components/ui-kit/empty-state";
import { Star } from "lucide-react";
import { Box, useTheme } from "@mui/material";
import { useLeadRatings } from "@/modules/lead-page/new/hooks/ratings/use-lead-reviews.hook";
import RatingCard from "@/modules/lead-page/new/components/ratings/rating-card/rating-card";
import RatingCardSkeleton from "@/modules/lead-page/new/components/ratings/rating-card/rating-card-skeleton";
import { Rating } from "@/modules/lead-page/types";
import { SortOrder } from "@/modules/common/components/input/sort-input-toggle";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { LeadPageTab } from "@/modules/lead-page/new/constants";
import { useActivityContext } from "@/modules/lead-page/new/hooks/activity-context.hook";
import useActivityTimeline from "@/modules/lead-page/new/hooks/activity-timline.hook";
import { useFindInActivity } from "@/modules/lead-page/new/hooks/use-find-in-activity.hook";

interface ReviewsContentProps {
  ratingFilter?: number;
  sortOrder: SortOrder;
  onHasRatingsChange?: (hasRatings: boolean) => void;
  onTabChange?: (tabId: string) => void;
}

const ReviewsTab: React.FC<ReviewsContentProps> = ({
  ratingFilter,
  sortOrder,
  onHasRatingsChange,
  onTabChange,
}) => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lead } = useContext(LeadPageContext);
  const { setHighlightedActivityId } = useActivityContext();
  const { allItems } = useActivityTimeline();
  const { findInActivity } = useFindInActivity();

  const reviewIdFromUrl = searchParams.get("reviewId");
  const [highlightedReviewId, setHighlightedReviewId] = useState<string | null>(
    null,
  );
  const hasScrolledRef = useRef(false);

  // Track if we've ever confirmed that rating data exists
  const hasConfirmedDataExistsRef = useRef(false);

  // Map sort order to API parameters
  const sortParams = useMemo(() => {
    const direction = sortOrder === "newest" ? "DESC" : "ASC";
    return {
      sort: `createdAt:${direction}`,
    };
  }, [sortOrder]);

  // Fetch ratings with filter
  const {
    ratings,
    isLoading,
    isError,
    refetch: refetchRatings,
    fetchUntilReviewFound,
  } = useLeadRatings({
    filters: {
      ...(lead?.data?.lead?.journey_uid && {
        journeyUIds: lead.data.lead.journey_uid,
      }),
      ...(ratingFilter !== undefined && { ratings: [ratingFilter] }),
      page: 1,
      pageSize: 20,
      ...sortParams,
    },
  });

  // Update confirmation when we get results
  useEffect(() => {
    if (!isLoading && ratings && ratings.length > 0) {
      hasConfirmedDataExistsRef.current = true;
    }
  }, [ratings, isLoading]);

  // Determine if there's any rating data at all
  const hasAnyRatings = useMemo(() => {
    // If we've ever seen data, it exists
    if (hasConfirmedDataExistsRef.current) return true;
    // Otherwise check current results (when no filter is applied initially)
    return !isLoading && ratings && ratings.length > 0;
  }, [ratings, isLoading]);

  // Notify parent about data existence
  useEffect(() => {
    if (onHasRatingsChange && !isLoading) {
      onHasRatingsChange(hasAnyRatings);
    }
  }, [hasAnyRatings, onHasRatingsChange, isLoading]);

  const scrollToAndHighlightReview = useCallback(
    (reviewId: string) => {
      setHighlightedReviewId(reviewId);

      setTimeout(() => {
        const element = document.getElementById(`rating-card-${reviewId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 150);

      setTimeout(() => {
        setHighlightedReviewId(null);
        searchParams.delete("reviewId");
        setSearchParams(searchParams, { replace: true });
      }, 3000);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!reviewIdFromUrl || isLoading || !ratings || hasScrolledRef.current)
      return;

    const reviewExists = ratings.some((r: Rating) => r.uid === reviewIdFromUrl);

    if (reviewExists || ratings.length > 0) {
      hasScrolledRef.current = true;

      if (reviewExists) {
        scrollToAndHighlightReview(reviewIdFromUrl);
      } else {
        fetchUntilReviewFound(reviewIdFromUrl).then((found) => {
          if (found) {
            setTimeout(() => scrollToAndHighlightReview(reviewIdFromUrl), 200);
          } else {
            searchParams.delete("reviewId");
            setSearchParams(searchParams, { replace: true });
          }
        });
      }
    }
  }, [
    reviewIdFromUrl,
    isLoading,
    ratings,
    fetchUntilReviewFound,
    scrollToAndHighlightReview,
    searchParams,
    setSearchParams,
  ]);

  // Find in Activity handler
  const handleFindInActivity = useCallback(
    async (ratingUId: string, createdAt?: string | Date) => {
      if (!createdAt) {
        // Fallback to old behavior if createdAt is not provided
        const activityItem = allItems.find((item) => item.uid === ratingUId);

        if (activityItem) {
          const activityId = `${activityItem.id}-${activityItem.uid}`;
          setHighlightedActivityId?.(activityId);

          if (onTabChange) {
            onTabChange(LeadPageTab.ACTIVITY);
          }

          setTimeout(() => {
            const element = document.getElementById(`activity-${activityId}`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }, 300);
        }
        return;
      }

      // Switch to Activity tab IMMEDIATELY for instant navigation
      if (onTabChange) {
        onTabChange(LeadPageTab.ACTIVITY);
      }

      // Fetch activities in the background (non-blocking)
      // This allows navigation to happen instantly while data loads
      findInActivity(ratingUId, createdAt).catch((error) => {
        console.error("Error finding activity:", error);
      });
    },
    [allItems, setHighlightedActivityId, onTabChange, findInActivity],
  );

  const noRatings =
    !isLoading && !isError && (!ratings || ratings.length === 0);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <RatingCardSkeleton />
        <RatingCardSkeleton />
      </Box>
    );
  }

  if (noRatings) {
    return (
      <EmptyState
        icon={<Star />}
        label={
          ratingFilter !== undefined ? "No matching reviews" : "No reviews yet"
        }
        description={
          ratingFilter !== undefined
            ? "No reviews match your current filter."
            : "This lead has not reviewed any agents yet."
        }
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<Star />}
        label="Error loading reviews"
        description="There was an error fetching reviews data."
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        height: "100%",
        overflowY: "auto",
      }}
    >
      {ratings?.map((rating: Rating) => (
        <RatingCard
          key={rating?.uid}
          id={`rating-card-${rating?.uid}`}
          ratingUId={rating?.uid || ""}
          agent={rating?.agent}
          rating={rating?.rating}
          comment={rating?.comment}
          comments={rating?.comments}
          touchpoint={rating?.status?.name}
          followUpStatus={rating?.followUpStatus}
          createdAt={rating?.createdAt}
          findInActivity={() =>
            handleFindInActivity(rating?.uid || "", rating?.createdAt)
          }
          onResolvedFollowUp={refetchRatings}
          isHighlighted={highlightedReviewId === rating?.uid}
        />
      ))}
    </Box>
  );
};

export default ReviewsTab;
